import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Settings, Edit, Eye, EyeOff, Palette, Layout, Type, Image as ImageIcon,
  Save, X, ChevronRight, Monitor, Smartphone, Tablet, Menu, Plus
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const APPS_CONFIG = [
  {
    id: 'digi-connect',
    name: 'Digi Connect',
    description: 'Digital Business Card & Networking',
    icon: '💼',
    route: '/digi-connect',
    features: ['Digital Cards', 'QR Code', 'Contact Sharing', 'Analytics']
  },
  {
    id: 'digi-branding',
    name: 'Digi Branding',
    description: 'Canva-style Design Editor',
    icon: '🎨',
    route: '/digi-branding',
    features: ['Template Gallery', 'Drag & Drop Editor', 'Stock Images']
  },
  {
    id: 'digi-editor',
    name: 'Digi Editor',
    description: 'Standalone Poster Editor',
    icon: '✨',
    route: '/digi-editor',
    features: ['Templates', 'Business Info', 'Export PNG']
  },
  {
    id: 'digi-strategy',
    name: 'Digi Strategy',
    description: 'AI Strategy Generator with Editor',
    icon: '🎯',
    route: '/digi-strategy',
    features: ['AI Strategy', 'Social Posts', 'Poster Editor']
  },
  {
    id: 'hr-payroll',
    name: 'HR & Payroll',
    description: 'Employee & Salary Management',
    icon: '👥',
    route: '/hr-payroll',
    features: ['Employee Management', 'Salary Processing', 'Reports']
  },
  {
    id: 'crm',
    name: 'CRM',
    description: 'Lead Management System',
    icon: '📊',
    route: '/crm',
    features: ['Lead Tracking', 'Pipeline Management', 'Analytics']
  },
  {
    id: 'events',
    name: 'Events',
    description: 'Event Scheduling & Management',
    icon: '📅',
    route: '/events',
    features: ['Event Creation', 'Calendar View', 'Attendee Management']
  },
  {
    id: 'presentor',
    name: 'Digi Presentor',
    description: 'AI Presentation Script Generator',
    icon: '📢',
    route: '/presentor',
    features: ['AI Scripts', 'Notifications', 'Subscription']
  }
];

export default function AdminAppsManager({ onBack }) {
  const [apps, setApps] = useState([]);
  const [selectedApp, setSelectedApp] = useState(null);
  const [appSettings, setAppSettings] = useState({});
  const [showEditor, setShowEditor] = useState(false);

  useEffect(() => {
    loadApps();
  }, []);

  const loadApps = async () => {
    try {
      const response = await axios.get(`${API}/admin/apps`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
      });
      setApps(response.data.apps || APPS_CONFIG);
    } catch (error) {
      // Use default config if API fails
      setApps(APPS_CONFIG);
    }
  };

  const toggleApp = async (appId, enabled) => {
    try {
      await axios.put(
        `${API}/admin/apps/${appId}`,
        { enabled },
        { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } }
      );
      toast.success(enabled ? 'App enabled' : 'App disabled');
      loadApps();
    } catch (error) {
      toast.error('Failed to update app status');
    }
  };

  const openAppEditor = (app) => {
    setSelectedApp(app);
    setAppSettings({
      enabled: true,
      primaryColor: '#7C3AED',
      secondaryColor: '#EC4899',
      headerTitle: app.name,
      description: app.description,
      showInNav: true,
      customCSS: '',
      features: app.features || []
    });
    setShowEditor(true);
  };

  const saveAppSettings = async () => {
    try {
      await axios.put(
        `${API}/admin/apps/${selectedApp.id}/settings`,
        appSettings,
        { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } }
      );
      toast.success('Settings saved successfully!');
      setShowEditor(false);
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  if (showEditor && selectedApp) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <Button onClick={() => setShowEditor(false)} variant="ghost" className="mb-2">
              ← Back to Apps
            </Button>
            <h2 className="text-3xl font-bold text-gray-900">{selectedApp.icon} {selectedApp.name}</h2>
            <p className="text-gray-500">Customize appearance and settings</p>
          </div>
          <Button onClick={saveAppSettings} className="bg-purple-600 hover:bg-purple-700">
            <Save className="w-4 h-4 mr-2" />
            Save Changes
          </Button>
        </div>

        <div className="grid grid-cols-3 gap-6">
          {/* Settings Panel */}
          <div className="col-span-2 space-y-6">
            {/* Basic Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Basic Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">App Status</label>
                  <div className="flex items-center gap-4">
                    <Button
                      variant={appSettings.enabled ? 'default' : 'outline'}
                      onClick={() => setAppSettings({ ...appSettings, enabled: true })}
                      className={appSettings.enabled ? 'bg-green-600' : ''}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Enabled
                    </Button>
                    <Button
                      variant={!appSettings.enabled ? 'default' : 'outline'}
                      onClick={() => setAppSettings({ ...appSettings, enabled: false })}
                      className={!appSettings.enabled ? 'bg-red-600' : ''}
                    >
                      <EyeOff className="w-4 h-4 mr-2" />
                      Disabled
                    </Button>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Header Title</label>
                  <Input
                    value={appSettings.headerTitle}
                    onChange={(e) => setAppSettings({ ...appSettings, headerTitle: e.target.value })}
                    placeholder="App header title"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Description</label>
                  <textarea
                    value={appSettings.description}
                    onChange={(e) => setAppSettings({ ...appSettings, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    rows="3"
                    placeholder="App description"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={appSettings.showInNav}
                    onChange={(e) => setAppSettings({ ...appSettings, showInNav: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <label className="text-sm font-medium text-gray-700">Show in Navigation</label>
                </div>
              </CardContent>
            </Card>

            {/* Theme & Styling */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Palette className="w-5 h-5" />
                  Theme & Colors
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-2">Primary Color</label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={appSettings.primaryColor}
                        onChange={(e) => setAppSettings({ ...appSettings, primaryColor: e.target.value })}
                        className="w-16 h-10"
                      />
                      <Input
                        type="text"
                        value={appSettings.primaryColor}
                        onChange={(e) => setAppSettings({ ...appSettings, primaryColor: e.target.value })}
                        className="flex-1"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-2">Secondary Color</label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={appSettings.secondaryColor}
                        onChange={(e) => setAppSettings({ ...appSettings, secondaryColor: e.target.value })}
                        className="w-16 h-10"
                      />
                      <Input
                        type="text"
                        value={appSettings.secondaryColor}
                        onChange={(e) => setAppSettings({ ...appSettings, secondaryColor: e.target.value })}
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Custom CSS</label>
                  <textarea
                    value={appSettings.customCSS}
                    onChange={(e) => setAppSettings({ ...appSettings, customCSS: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                    rows="6"
                    placeholder=".custom-class { color: red; }"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Features */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Layout className="w-5 h-5" />
                  Features & Modules
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {appSettings.features?.map((feature, index) => (
                    <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                      <Input
                        value={feature}
                        onChange={(e) => {
                          const newFeatures = [...appSettings.features];
                          newFeatures[index] = e.target.value;
                          setAppSettings({ ...appSettings, features: newFeatures });
                        }}
                        className="flex-1 mr-2"
                        placeholder="Feature name"
                      />
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => {
                          const newFeatures = appSettings.features.filter((_, i) => i !== index);
                          setAppSettings({ ...appSettings, features: newFeatures });
                        }}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    onClick={() => {
                      setAppSettings({
                        ...appSettings,
                        features: [...(appSettings.features || []), 'New Feature']
                      });
                    }}
                    variant="outline"
                    className="w-full"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Feature
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Preview Panel */}
          <div className="col-span-1">
            <Card className="sticky top-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Monitor className="w-5 h-5" />
                  Live Preview
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="border-2 rounded-lg p-4 bg-gray-50">
                  <div 
                    className="p-4 rounded-lg text-white mb-4"
                    style={{ backgroundColor: appSettings.primaryColor }}
                  >
                    <h3 className="font-bold text-lg">{appSettings.headerTitle}</h3>
                    <p className="text-sm opacity-90">{appSettings.description}</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Button 
                      className="w-full"
                      style={{ backgroundColor: appSettings.primaryColor }}
                    >
                      Primary Button
                    </Button>
                    <Button 
                      className="w-full"
                      style={{ backgroundColor: appSettings.secondaryColor }}
                    >
                      Secondary Button
                    </Button>
                  </div>

                  <div className="mt-4 text-xs text-gray-500">
                    <p>Status: {appSettings.enabled ? '✅ Enabled' : '❌ Disabled'}</p>
                    <p>Navigation: {appSettings.showInNav ? '✅ Visible' : '❌ Hidden'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Apps Management</h2>
        <p className="text-gray-500">Configure and customize all your applications</p>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {APPS_CONFIG.map((app) => (
          <Card key={app.id} className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="text-4xl">{app.icon}</div>
                  <div>
                    <CardTitle className="text-lg">{app.name}</CardTitle>
                    <p className="text-sm text-gray-500">{app.description}</p>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  {app.features.map((feature, idx) => (
                    <span key={idx} className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                      {feature}
                    </span>
                  ))}
                </div>

                <div className="flex gap-2 pt-2">
                  <Button
                    onClick={() => openAppEditor(app)}
                    className="flex-1 bg-purple-600 hover:bg-purple-700"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit App
                  </Button>
                  <Button variant="outline" className="flex-1">
                    <Eye className="w-4 h-4 mr-2" />
                    Preview
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
